import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Re-enabled for navigation
import { useAuth } from '../../contexts/AuthContext'; // Assuming this path is correct

const NewQuotePage = () => {
  const navigate = useNavigate();
  const { token } = useAuth(); // Removed unused 'user'

  const [formData, setFormData] = useState({
    clientId: null,
    // serviceRequestId: null, // Add if quotes can be linked to service requests
    quoteNumber: `QOT-${Date.now()}`, // Generate quote number
    quoteDate: new Date().toISOString().split('T')[0],
    expiryDate: '',
    lineItems: [
      { description: '', quantity: 1, unitPrice: 0, total: 0 },
    ],
    subTotal: 0,
    taxRate: 0.0825, // Default tax rate
    taxAmount: 0,
    totalAmount: 0,
    status: 'draft',
    notes: '',
    termsAndConditions: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [clients, setClients] = useState([]);

  useEffect(() => {
    const fetchClients = async () => {
      if (!token) return;
      try {
        // Assuming your API client is set up or use fetch directly like in estimates
        const response = await fetch('/api/users?role=client', { // Ensure this endpoint is correct
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch clients');
        const data = await response.json();
        if (data.status === 'success') {
          setClients(data.data.users || []);
        } else {
          throw new Error(data.message || 'Could not parse client data');
        }
      } catch (err) {
        console.error('Error fetching clients:', err);
        // setError('Failed to load clients. Please try again.'); // User-facing error
      }
    };
    fetchClients();
  }, [token]);

  useEffect(() => {
    let currentSubTotal = 0;
    formData.lineItems.forEach(item => {
      currentSubTotal += parseFloat(item.total || 0);
    });
    const currentTaxAmount = currentSubTotal * parseFloat(formData.taxRate || 0);
    const currentTotalAmount = currentSubTotal + currentTaxAmount;

    setFormData(prev => ({
      ...prev,
      subTotal: currentSubTotal,
      taxAmount: currentTaxAmount,
      totalAmount: currentTotalAmount,
    }));
  }, [formData.lineItems, formData.taxRate]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
       ...prev,
       [name]: type === 'number' ? parseFloat(value) : value 
    }));
  };

  const handleLineItemChange = (index, e) => {
    const { name, value, type } = e.target;
    const updatedLineItems = [...formData.lineItems];
    const item = updatedLineItems[index];
    
    // Ensure item exists, if not, initialize it (defensive coding)
    if (!item) updatedLineItems[index] = { description: '', quantity: 1, unitPrice: 0, total: 0 };
    
    updatedLineItems[index][name] = type === 'number' ? parseFloat(value) : value;
    
    if (name === 'quantity' || name === 'unitPrice') {
      updatedLineItems[index].total = parseFloat(updatedLineItems[index].quantity || 0) * parseFloat(updatedLineItems[index].unitPrice || 0);
    }
    setFormData(prev => ({ ...prev, lineItems: updatedLineItems }));
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { description: '', quantity: 1, unitPrice: 0, total: 0 }],
    }));
  };

  const removeLineItem = (index) => {
    const updatedLineItems = [...formData.lineItems];
    updatedLineItems.splice(index, 1);
    setFormData(prev => ({ ...prev, lineItems: updatedLineItems }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (!formData.clientId) {
        setError('Please select a client.');
        setIsLoading(false);
        return;
    }
    if (formData.lineItems.some(item => !item.description || item.quantity <= 0 || item.unitPrice < 0)) { // Allow 0 unit price
        setError('Please ensure all line items have a description and positive quantity.');
        setIsLoading(false);
        return;
    }

    const payload = {
        ...formData,
        clientId: parseInt(formData.clientId, 10),
        quoteDate: new Date(formData.quoteDate).toISOString(),
        expiryDate: formData.expiryDate ? new Date(formData.expiryDate).toISOString() : null,
        // Ensure line items are correctly formatted if backend expects numbers for quantity/unitPrice/total
        lineItems: formData.lineItems.map(item => ({
            ...item,
            quantity: parseFloat(item.quantity || 0),
            unitPrice: parseFloat(item.unitPrice || 0),
            total: parseFloat(item.total || 0),
        })),
        taxRate: parseFloat(formData.taxRate || 0),
    };

    console.log('Submitting Quote:', payload); // For debugging

    try {
      // Using fetch like in CreateEstimatePage for consistency, replace with apiClient if preferred
      const response = await fetch('/api/quotes', { // Ensure this is your correct quotes API endpoint
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to create quote');
      }
      
      alert('Quote created successfully!'); // Simple feedback, consider a more integrated notification
      if (responseData.data && responseData.data.quote && responseData.data.quote.id) {
        navigate(`/admin/quotes/view/${responseData.data.quote.id}`); // Navigate to view the created quote
      } else {
        navigate('/admin/quotes'); // Fallback navigation
      }

    } catch (err) {
      console.error('Error creating quote:', err);
      setError(err.message || 'An error occurred while creating the quote.');
    } finally {
      setIsLoading(false);
    }
  };

  // CSS classes for consistency with CreateEstimatePage
  const inputClass = "mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm";
  const inputSmClass = "mt-1 block w-full input-sm-class"; // Define or ensure this class exists, using placeholder from estimate

  return (
    <div className="container mx-auto px-4 py-8">
      <header className="mb-8">
        <h1 className="text-4xl font-bold text-brand-text-dark">Create New Quote</h1>
        <button 
          onClick={() => navigate(-1)} // Navigate back
          className="text-sm text-blue-600 hover:text-blue-800 mt-2"
        >
          &larr; Back to Quotes
        </button>
      </header>

      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg p-6 md:p-8">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        <div className="mb-6">
            <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 mb-1">Client</label>
            <select
                name="clientId"
                id="clientId"
                value={formData.clientId || ''}
                onChange={handleChange}
                className={inputClass}
                required
            >
                <option value="" disabled>Select a client</option>
                {clients.map(client => (
                    <option key={client.id} value={client.id}>
                        {client.username} ({client.email})
                    </option>
                ))}
            </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label htmlFor="quoteNumber" className="block text-sm font-medium text-gray-700 mb-1">Quote Number</label>
            <input type="text" name="quoteNumber" id="quoteNumber" value={formData.quoteNumber} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label htmlFor="quoteDate" className="block text-sm font-medium text-gray-700 mb-1">Quote Date</label>
            <input type="date" name="quoteDate" id="quoteDate" value={formData.quoteDate} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700 mb-1">Expiry Date (Optional)</label>
            <input type="date" name="expiryDate" id="expiryDate" value={formData.expiryDate} onChange={handleChange} className={inputClass} />
          </div>
           <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select name="status" id="status" value={formData.status} onChange={handleChange} className={inputClass}>
                <option value="draft">Draft</option>
                <option value="sent_to_client">Sent to Client</option>
                {/* Add other relevant statuses for quotes if needed */}
            </select>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Line Items</h3>
          {formData.lineItems.map((item, index) => (
            <div key={index} className="grid grid-cols-12 gap-x-3 gap-y-2 items-end p-3 mb-3 border rounded-md">
              <div className="col-span-12 sm:col-span-5">
                <label htmlFor={`itemDesc-${index}`} className="block text-xs font-medium text-gray-600">Description</label>
                <input type="text" name="description" id={`itemDesc-${index}`} value={item.description} onChange={(e) => handleLineItemChange(index, e)} className={inputSmClass} placeholder="Service or Item" required />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label htmlFor={`itemQty-${index}`} className="block text-xs font-medium text-gray-600">Quantity</label>
                <input type="number" name="quantity" id={`itemQty-${index}`} value={item.quantity} onChange={(e) => handleLineItemChange(index, e)} className={`${inputSmClass} text-right`} min="0.01" step="0.01" required />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label htmlFor={`itemPrice-${index}`} className="block text-xs font-medium text-gray-600">Unit Price</label>
                <input type="number" name="unitPrice" id={`itemPrice-${index}`} value={item.unitPrice} onChange={(e) => handleLineItemChange(index, e)} className={`${inputSmClass} text-right`} min="0.00" step="0.01" required />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label htmlFor={`itemTotal-${index}`} className="block text-xs font-medium text-gray-600">Item Total</label>
                <input type="number" name="total" id={`itemTotal-${index}`} value={item.total.toFixed(2)} readOnly className={`${inputSmClass} text-right bg-gray-50`} />
              </div>
              <div className="col-span-12 sm:col-span-1 flex justify-end">
                {formData.lineItems.length > 1 && (
                  <button type="button" onClick={() => removeLineItem(index)} className="text-red-500 hover:text-red-700 p-1 rounded-md text-sm">Remove</button>
                )}
              </div>
            </div>
          ))}
          <button type="button" onClick={addLineItem} className="mt-2 text-blue-600 hover:text-blue-800 font-medium text-sm py-2 px-3 border border-blue-500 rounded-md hover:bg-blue-50 transition-colors">
            + Add Line Item
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 border-t pt-6 mt-6">
            <div>
                <label htmlFor="subTotal" className="block text-sm font-medium text-gray-500">Subtotal</label>
                <input type="text" name="subTotal" id="subTotal" value={formData.subTotal.toFixed(2)} readOnly className="mt-1 block w-full input-class bg-gray-50 text-right" />
            </div>
            <div>
                <label htmlFor="taxRate" className="block text-sm font-medium text-gray-700">Tax Rate</label>
                <input type="number" name="taxRate" id="taxRate" value={formData.taxRate} onChange={handleChange} className={`${inputClass} text-right`} step="0.0001"/>
            </div>
            <div>
                <label htmlFor="taxAmount" className="block text-sm font-medium text-gray-500">Tax Amount</label>
                <input type="text" name="taxAmount" id="taxAmount" value={formData.taxAmount.toFixed(2)} readOnly className="mt-1 block w-full input-class bg-gray-50 text-right" />
            </div>
        </div>
        
        <div className="text-right mb-6 border-t pt-4">
            <p className="text-sm text-gray-500">Total Amount</p>
            <p className="text-2xl font-bold text-gray-800">${formData.totalAmount.toFixed(2)}</p>
        </div>

        <div className="mb-6">
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
          <textarea name="notes" id="notes" value={formData.notes} onChange={handleChange} rows="3" className={inputClass} placeholder="Internal notes or notes for the client..."></textarea>
        </div>

        <div className="mb-8">
          <label htmlFor="termsAndConditions" className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions (Optional)</label>
          <textarea name="termsAndConditions" id="termsAndConditions" value={formData.termsAndConditions} onChange={handleChange} rows="3" className={inputClass} placeholder="Define payment terms, scope of work, etc..."></textarea>
        </div>
        
        <div className="flex justify-end pt-4 border-t">
          <button 
            type="button" 
            onClick={() => navigate('/admin/quotes')} // Or navigate(-1)
            className="mr-3 px-6 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={isLoading}
          >
            {isLoading ? 'Creating...' : 'Create Quote'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewQuotePage; 